import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Box, Modal, styled } from "@mui/material";
import type {
  CelebrationItem,
  CelebrationProcessor,
  ShadeProcessor,
  ShadeRenderContext,
} from "./types";

type CelebrationContextValue = {
  upsert: (processor: CelebrationProcessor) => void;
  remove: (id: string) => void;
};

const CelebrationContext = createContext<CelebrationContextValue | null>(null);

export const useCelebrationProcessor = (
  processor: CelebrationProcessor | null,
) => {
  const ctx = useContext(CelebrationContext);
  if (!ctx) {
    throw new Error(
      "useCelebrationProcessor must be used inside <CelebrationCoordinator>",
    );
  }

  // Re-register on every change to the processor's payload. Consumers are
  // expected to memoize items / renderItem / onAcknowledge so this only
  // fires when something meaningful changed.
  useEffect(() => {
    if (!processor) return undefined;
    ctx.upsert(processor);
    const id = processor.id;
    return () => ctx.remove(id);
  }, [ctx, processor]);
};

type Entry = {
  key: string;
  proc: ShadeProcessor;
  item: CelebrationItem;
  render: (ctx: ShadeRenderContext) => ReactNode;
  acknowledge: () => void;
};

// Wait long enough for any section's per-item "leaving" animation to play
// out before unmounting the modal. Sections currently use 1000ms — keep
// these in sync if you change one.
const SHADE_EXIT_DURATION_MS = 1000;

const ScrollContainer = styled(Box, { label: "CelebrationScrollContainer" })(
  () => ({
    position: "relative",
    height: "100vh",
    width: "100vw",
    overflowY: "auto",
    scrollSnapType: "y mandatory",
  }),
);

const Section = styled(Box, { label: "CelebrationSection" })(() => ({
  position: "relative",
  minHeight: "100vh",
  width: "100%",
  scrollSnapAlign: "start",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
  boxSizing: "border-box",
}));

type Props = { children: ReactNode };

const buildOpenSignature = (processors: CelebrationProcessor[]): string => {
  // Captures the data the sync effect cares about: per-processor status,
  // in-place active-ness, and shade item ids + priorities. If nothing in
  // this string changes, there's no reason to re-evaluate.
  const parts: string[] = [];
  for (const p of processors) {
    if (p.mode === "in-place") {
      parts.push(`ip:${p.id}:${p.status}:${p.isActive ? 1 : 0}`);
    } else {
      const ids = p.items.map((i) => `${i.id}@${i.priority}`).join(",");
      parts.push(`sh:${p.id}:${p.status}:[${ids}]`);
    }
  }
  return parts.sort().join("|");
};

const buildLiveEntries = (
  processors: CelebrationProcessor[],
  dismissed: Set<string>,
): Entry[] => {
  const out: Entry[] = [];
  const seen = new Set<string>();
  for (const proc of processors) {
    if (proc.mode !== "shade") continue;
    for (const item of proc.items) {
      const key = `${proc.id}::${item.id}`;
      if (seen.has(key) || dismissed.has(key)) continue;
      seen.add(key);
      out.push({
        key,
        proc,
        item,
        render: (ctx) => proc.renderItem(item, ctx),
        acknowledge: () => proc.onAcknowledge(item),
      });
    }
  }
  return out;
};

const sameKeys = (a: Entry[], b: Entry[]): boolean => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i].key !== b[i].key) return false;
  }
  return true;
};

export const CelebrationCoordinator = ({ children }: Props) => {
  const processorsRef = useRef(new Map<string, CelebrationProcessor>());
  const [, setTick] = useState(0);
  const bump = useCallback(() => setTick((t) => t + 1), []);

  const upsert = useCallback(
    (p: CelebrationProcessor) => {
      processorsRef.current.set(p.id, p);
      bump();
    },
    [bump],
  );
  const remove = useCallback(
    (id: string) => {
      if (processorsRef.current.delete(id)) bump();
    },
    [bump],
  );

  const ctxValue = useMemo<CelebrationContextValue>(
    () => ({ upsert, remove }),
    [upsert, remove],
  );

  // Session-scoped record of every (processor.id, item.id) the user has
  // dismissed. Persists for the life of the page so a celebration cannot
  // reappear in this session even if upstream detection re-pushes it.
  const dismissedKeysRef = useRef<Set<string>>(new Set());

  const processors = Array.from(processorsRef.current.values());
  const openSignature = buildOpenSignature(processors);

  // The list of celebrations currently rendered in the shade. Indices
  // 0..activeIndex are "frozen" — they were already shown (or are showing
  // now) and cannot be reordered or removed by late arrivals. Anything
  // beyond activeIndex is the "tail" and gets re-sorted by item priority
  // every time the live processor item set changes, so a late-arriving
  // shout-out can slot in between two queued items based on priority,
  // but it can never jump ahead of the celebration the user is on.
  const [displayedList, setDisplayedList] = useState<Entry[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  // Keep displayedList in sync with the live processor state.
  useEffect(() => {
    const procs = Array.from(processorsRef.current.values());
    const liveEntries = buildLiveEntries(procs, dismissedKeysRef.current);

    setDisplayedList((prev) => {
      if (prev.length === 0) {
        // Shade is closed. Initial open is gated on all-ready + no in-
        // place celebration active + at least one item to show.
        const allReady = procs.every((p) => p.status === "ready");
        const inPlaceActive = procs.some(
          (p) => p.mode === "in-place" && p.isActive,
        );
        if (!allReady || inPlaceActive || liveEntries.length === 0) {
          return prev;
        }
        return [...liveEntries].sort(
          (a, b) => a.item.priority - b.item.priority,
        );
      }

      // Shade is open. Freeze the prefix up to and including the active
      // index — those are the ones the user has already encountered.
      // Re-sort the rest by priority so late items slot in correctly,
      // but never ahead of the active one.
      const frozenIdx = Math.min(activeIndex, prev.length - 1);
      const frozenPrefix = prev.slice(0, frozenIdx + 1);
      const frozenKeys = new Set(frozenPrefix.map((e) => e.key));
      const tail = liveEntries
        .filter((e) => !frozenKeys.has(e.key))
        .sort((a, b) => a.item.priority - b.item.priority);
      const next = [...frozenPrefix, ...tail];
      if (sameKeys(prev, next)) return prev;
      return next;
    });
  }, [openSignature, activeIndex]);

  const sectionRefs = useRef<Array<HTMLElement | null>>([]);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  // Block user-initiated scrolling inside the shade. Manually scrolling
  // past a section would skip its acknowledge callback (which only fires
  // from the section's Next/Close button), so the only legal way to move
  // between celebrations is via onAdvance. We still need overflow: auto
  // on the container so programmatic scrollIntoView keeps working.
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return undefined;
    const block = (e: Event) => {
      e.preventDefault();
    };
    const blockKey = (e: KeyboardEvent) => {
      const keys = [
        "ArrowDown",
        "ArrowUp",
        "PageDown",
        "PageUp",
        "Home",
        "End",
        " ",
      ];
      if (keys.includes(e.key)) e.preventDefault();
    };
    el.addEventListener("wheel", block, { passive: false });
    el.addEventListener("touchmove", block, { passive: false });
    el.addEventListener("keydown", blockKey);
    return () => {
      el.removeEventListener("wheel", block);
      el.removeEventListener("touchmove", block);
      el.removeEventListener("keydown", blockKey);
    };
  }, [displayedList.length]);

  // When activeIndex changes, scroll the new section into view.
  useLayoutEffect(() => {
    if (displayedList.length === 0) return;
    if (activeIndex >= displayedList.length) return;
    const el = sectionRefs.current[activeIndex];
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [activeIndex, displayedList]);

  const advance = useCallback(() => {
    const current = displayedList[activeIndex];
    // Already past the last item, so the close is pending and there is
    // nothing left to advance to. Returning IS the behaviour: activeIndex is
    // a dependency of the close effect below, so incrementing it again would
    // clear the pending exit timer and arm a fresh one. A climber clicking
    // Close faster than once a second used to keep the shade open
    // indefinitely, having acknowledged on the very first click.
    if (!current) return;
    // Guard against rapid double-clicks firing acknowledge twice before
    // setActiveIndex commits.
    if (dismissedKeysRef.current.has(current.key)) return;
    dismissedKeysRef.current.add(current.key);
    try {
      current.acknowledge();
    } catch (err) {
      console.error("Celebration acknowledge failed", err);
    }
    setActiveIndex((i) => i + 1);
  }, [displayedList, activeIndex]);

  // When the user has advanced past the last item, schedule the close.
  // If the displayedList grows during the wait (a late item lands in the
  // tail), this effect re-runs, the cleanup clears the timer, and the
  // newly-appended item becomes active — i.e. the close is canceled.
  useEffect(() => {
    if (displayedList.length === 0) return undefined;
    if (activeIndex < displayedList.length) return undefined;
    const t = setTimeout(() => {
      setDisplayedList([]);
      setActiveIndex(0);
    }, SHADE_EXIT_DURATION_MS);
    return () => clearTimeout(t);
  }, [activeIndex, displayedList.length]);

  const open = displayedList.length > 0;

  return (
    <CelebrationContext.Provider value={ctxValue}>
      {children}
      <Modal open={open} hideBackdrop aria-labelledby="celebration-shade">
        <Box
          id="celebration-shade"
          sx={{
            outline: "none",
            position: "fixed",
            inset: 0,
          }}
        >
          {/* Self-rendered backdrop — stays mounted as a single DOM node
              for the entire life of the shade so it never flickers
              between celebrations. MUI's default Backdrop slot was
              causing a brief fade-out on inner re-renders. */}
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(20, 5, 35, 0.55)",
              backdropFilter: "blur(16px)",
              WebkitBackdropFilter: "blur(16px)",
              pointerEvents: "none",
            }}
          />
          <Box sx={{ position: "fixed", inset: 0 }}>
            {open ? (
              <ScrollContainer ref={scrollContainerRef}>
                {displayedList.map((entry, i) => {
                  const isActive = i === activeIndex;
                  const isLast = i === displayedList.length - 1;
                  return (
                    <Section
                      key={entry.key}
                      ref={(el: HTMLElement | null) => {
                        sectionRefs.current[i] = el;
                      }}
                    >
                      {entry.render({ isActive, isLast, onAdvance: advance })}
                    </Section>
                  );
                })}
              </ScrollContainer>
            ) : null}
          </Box>
        </Box>
      </Modal>
    </CelebrationContext.Provider>
  );
};

export default CelebrationCoordinator;
